import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ShoppingBag, Shirt, Book, X, ArrowLeft, Star } from 'lucide-react';
import MerchPromoSection from './marketplace/MerchPromoSection';
import GearEquipmentSection from './marketplace/GearEquipmentSection';
import PublicMarketplaceSection from './marketplace/PublicMarketplaceSection';
import GearChoiceModal from './marketplace/GearChoiceModal';
import GearReviewsSection from './marketplace/GearReviewsSection';
import DirectoryResourcesSection from './marketplace/DirectoryResourcesSection';

const MarketplaceTab = ({ onShareToDiscord }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [gearSubSection, setGearSubSection] = useState(null); // 'community' or 'public' or 'reviews' or null (choice modal)
  
  // Get current user from localStorage
  const getCurrentUser = () => {
    const userStr = localStorage.getItem('beatseek_user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch (e) {
        console.error('Failed to parse user:', e);
        return null;
      }
    }
    return null;
  };
  
  const currentUser = getCurrentUser();

  // Category cards data
  const categories = [
    {
      id: 'gear',
      title: 'Gear & Equipment',
      icon: ShoppingBag,
      description: 'Buy, sell, and trade DJ equipment',
      color: '#00ffff',
      gradient: 'linear-gradient(135deg, #00ffff, #0080ff)',
      features: [
        'Turntables, mixers, controllers',
        'Speakers, headphones, accessories',
        'Equipment rental listings',
        'Local pickup or shipping options'
      ]
    },
    {
      id: 'merch',
      title: 'Merch',
      icon: Shirt,
      description: 'Showcase and discover promotional merchandise',
      color: '#ff00ff',
      gradient: 'linear-gradient(135deg, #ff00ff, #00ffff)',
      features: [
        'DJ/Producer branded merchandise',
        'Nightclub & event promo items',
        'Sample packs & digital products',
        'Featured products with external links'
      ]
    },
    {
      id: 'directory',
      title: 'Directory & Resources',
      icon: Book,
      description: 'Find venues, services, and community resources',
      color: '#ffa500',
      gradient: 'linear-gradient(135deg, #ffa500, #ff6b6b)',
      features: [
        'Venue directory with booking info',
        'Service providers (photo, design, etc.)',
        'Studio & rehearsal space rentals',
        'Educational resources & tools'
      ]
    }
  ];

  // Close category and return to main view
  const handleCloseCategory = () => {
    setSelectedCategory(null);
    setGearSubSection(null); // Reset gear sub-section
  };

  // Handle back to gear choice modal
  const handleBackToGearChoice = () => {
    setGearSubSection(null);
  };

  // Render category detail modal (placeholder for now)
  const renderCategoryModal = () => {
    if (!selectedCategory) return null;

    const category = categories.find(c => c.id === selectedCategory);
    const IconComponent = category.icon;

    return ReactDOM.createPortal(
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10001,
          padding: '1rem'
        }}
        onClick={handleCloseCategory}
      >
        <div
          style={{
            width: '95%',
            maxWidth: '1400px',
            height: '90vh',
            background: 'rgba(26, 26, 46, 0.98)',
            borderRadius: '20px',
            border: `3px solid ${gearSubSection === 'reviews' ? '#fbbf24' : category.color}`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            style={{
              padding: '1.5rem 2rem',
              borderBottom: `2px solid ${gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.3)' : category.color + '30'}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: gearSubSection === 'reviews' 
                ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.1), transparent)'
                : `linear-gradient(135deg, ${category.color}10, transparent)`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              {/* Back button for gear sub-sections */}
              {selectedCategory === 'gear' && gearSubSection && (
                <button
                  onClick={handleBackToGearChoice}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(0, 255, 255, 0.1)',
                    border: gearSubSection === 'reviews' ? '1px solid rgba(251, 191, 36, 0.3)' : '1px solid rgba(0, 255, 255, 0.3)',
                    color: gearSubSection === 'reviews' ? '#fbbf24' : '#00ffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(0, 255, 255, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(0, 255, 255, 0.1)';
                  }}
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  background: selectedCategory === 'gear' && gearSubSection === 'reviews' 
                    ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' 
                    : category.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: selectedCategory === 'gear' && gearSubSection === 'reviews'
                    ? '0 0 30px rgba(251, 191, 36, 0.4)'
                    : `0 0 30px ${category.color}40`
                }}
              >
                {selectedCategory === 'gear' && gearSubSection === 'reviews' ? (
                  <Star size={26} color="#fff" fill="#fff" />
                ) : (
                  <IconComponent size={26} color="#fff" />
                )}
              </div>
              <div>
                <h2
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: '700',
                    color: '#fff',
                    margin: 0
                  }}
                >
                  {selectedCategory === 'gear' && gearSubSection 
                    ? (gearSubSection === 'community' ? 'Community Listings' : 
                       gearSubSection === 'public' ? 'Public Marketplaces' : 
                       gearSubSection === 'reviews' ? 'Gear Reviews' : category.title)
                    : category.title}
                </h2>
                <p
                  style={{
                    fontSize: '0.9rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    margin: '0.25rem 0 0 0'
                  }}
                >
                  {selectedCategory === 'gear' && gearSubSection 
                    ? (gearSubSection === 'community' ? 'Buy & sell with BeatSeek members' : 
                       gearSubSection === 'public' ? 'Search Reverb, eBay & more' :
                       gearSubSection === 'reviews' ? 'Expert reviews from Digital DJ Tips' : category.description)
                    : category.description}
                </p>
              </div>
            </div>

            <button
              onClick={handleCloseCategory}
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                background: gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 0, 128, 0.2)',
                border: gearSubSection === 'reviews' ? '2px solid rgba(251, 191, 36, 0.4)' : '2px solid rgba(255, 0, 128, 0.4)',
                color: gearSubSection === 'reviews' ? '#fbbf24' : '#ff0080',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.4)' : 'rgba(255, 0, 128, 0.4)';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = gearSubSection === 'reviews' ? 'rgba(251, 191, 36, 0.2)' : 'rgba(255, 0, 128, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <X size={22} />
            </button>
          </div>

          {/* Content Area */}
          <div
            style={{
              flex: 1,
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            {/* Render appropriate section based on category */}
            {selectedCategory === 'merch' ? (
              <MerchPromoSection currentUser={currentUser} />
            ) : selectedCategory === 'gear' ? (
              /* Gear has a choice modal flow */
              gearSubSection === 'community' ? (
                <GearEquipmentSection currentUser={currentUser} />
              ) : gearSubSection === 'public' ? (
                <PublicMarketplaceSection currentUser={currentUser} />
              ) : gearSubSection === 'reviews' ? (
                <GearReviewsSection 
                  isExpanded={true} 
                  onClose={() => setGearSubSection(null)} 
                  isInline={true}
                  onShareToDiscord={onShareToDiscord}
                />
              ) : (
                <GearChoiceModal
                  onSelectCommunity={() => setGearSubSection('community')}
                  onSelectPublic={() => setGearSubSection('public')}
                  onSelectReviews={() => setGearSubSection('reviews')}
                  onClose={handleCloseCategory}
                />
              )
            ) : selectedCategory === 'directory' ? (
              <DirectoryResourcesSection />
            ) : null}
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div
      className="marketplace-tab"
      style={{
        padding: '2rem',
        color: '#fff'
      }}
    >
      {/* Category Cards Grid */}
      <div
        className="marketplace-cards-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem',
          maxWidth: '1400px',
          margin: '0 auto'
        }}
      >
        {categories.map((category) => {
          const IconComponent = category.icon;
          
          return (
            <div
              key={category.id}
              className="marketplace-card"
              onClick={() => setSelectedCategory(category.id)}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                padding: '2rem',
                border: `2px solid ${category.color}40`,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = `0 20px 60px ${category.color}40`;
                e.currentTarget.style.border = `2px solid ${category.color}`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.border = `2px solid ${category.color}40`;
              }}
            >
              {/* Gradient Background Effect */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '200px',
                  background: category.gradient,
                  opacity: 0.1,
                  pointerEvents: 'none'
                }}
              />

              {/* Icon */}
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '16px',
                  background: category.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.5rem',
                  boxShadow: `0 10px 30px ${category.color}40`,
                  position: 'relative'
                }}
              >
                <IconComponent size={40} color="#fff" />
              </div>

              {/* Title */}
              <h3
                style={{
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  color: '#fff',
                  marginBottom: '0.75rem',
                  position: 'relative'
                }}
              >
                {category.title}
              </h3>

              {/* Description */}
              <p
                style={{
                  fontSize: '1rem',
                  color: 'rgba(255, 255, 255, 0.7)',
                  marginBottom: '1.5rem',
                  position: 'relative'
                }}
              >
                {category.description}
              </p>

              {/* Features List */}
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  position: 'relative'
                }}
              >
                {category.features.map((feature, idx) => (
                  <li
                    key={idx}
                    style={{
                      fontSize: '0.9rem',
                      color: 'rgba(255, 255, 255, 0.6)',
                      marginBottom: '0.5rem',
                      paddingLeft: '1.5rem',
                      position: 'relative'
                    }}
                  >
                    <span
                      style={{
                        position: 'absolute',
                        left: 0,
                        color: category.color
                      }}
                    >
                      ✓
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* Click Indicator */}
              <div
                style={{
                  marginTop: '1.5rem',
                  fontSize: '0.9rem',
                  color: category.color,
                  fontWeight: '600',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  position: 'relative'
                }}
              >
                <span>Explore</span>
                <span>→</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Render category detail modal */}
      {renderCategoryModal()}
    </div>
  );
};

export default MarketplaceTab;
